import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { deleteReport, setReportExpenses, submitReport, type Outcome } from './api';
import type { Report } from './types';

const KNOWN = [
  'CATEGORY_NOT_ALLOWED_FOR_NOTE',
  'EXPENSE_OUT_OF_PERIOD',
  'OWNER_TAX_CODE_MISSING',
] as const;

export type ActionError = 'conflict' | (typeof KNOWN)[number] | 'other';

function failure(status: number, code: string): ActionError {
  if (status === 409 || status === 412) return 'conflict';
  return KNOWN.find((known) => known === code) ?? 'other';
}

/** The owner's actions on a report; each one answers with the report, or says what failed. */
export function useReportActions(
  report: Report,
  adopt: (report: Report) => void,
  reload: () => void,
) {
  const navigate = useNavigate();
  const { t } = useTranslation('expenses-reports');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);

  const run = async <T>(act: () => Promise<Outcome<T>>, done: (value: T) => void) => {
    setBusy(true);
    setError(null);
    const outcome = await act();
    setBusy(false);
    if (outcome.ok) {
      done(outcome.value);
      return;
    }
    const reason = failure(outcome.status, outcome.code);
    setError(reason);
    // The tax code may have been removed since the page was read: read it again for the reason.
    if (reason === 'conflict' || reason === 'OWNER_TAX_CODE_MISSING') reload();
  };

  const ids = report.expenses?.map((row) => row.id) ?? [];
  // Each action answers at once with a short toast (U9), after the server accepted it.
  const answered = (message: string) => (next: Report) => {
    adopt(next);
    toast.success(message);
  };
  const replaceExpenses = (next: readonly string[], message: string) =>
    run(() => setReportExpenses(report.id, report.version, next), answered(message));

  return {
    busy,
    error,
    add: (added: readonly string[]) => replaceExpenses([...ids, ...added], t('toast.added')),
    remove: (id: string) =>
      replaceExpenses(
        ids.filter((other) => other !== id),
        t('toast.removed'),
      ),
    submit: () =>
      run(() => submitReport(report.id, report.version), answered(t('toast.submitted'))),
    discard: () =>
      run(
        () => deleteReport(report.id),
        () => void navigate('/expenses/reports', { replace: true }),
      ),
  } as const;
}
